import { Alert, Button, Collapse, Form, Input, Radio, Typography } from "antd"
import { useEffect, useMemo, useState } from "react"
import { Link, useSearchParams } from "react-router-dom"
import { DesktopOutlined } from "@ant-design/icons"
import AuthShell from "../../Layout/AuthShell"
import apiClient from "../../Utils/apiClient"
import { lookupForOs } from "./enrollmentLookup"

const EnrollDevice = () => {
  const [searchParams] = useSearchParams()
  const token = useMemo(() => searchParams.get("token") || "", [searchParams])
  const [form] = Form.useForm()
  const [loading, setLoading] = useState(false)
  const [checking, setChecking] = useState(true)
  const [error, setError] = useState("")
  const [invite, setInvite] = useState(null)
  const [done, setDone] = useState(null)
  const selectedOs = Form.useWatch("osPlatform", form) || "windows"
  const lookup = lookupForOs(selectedOs)

  useEffect(() => {
    let cancelled = false
    const verify = async () => {
      if (!token) {
        setChecking(false)
        setError("This enrollment link is missing or incomplete.")
        return
      }
      try {
        const res = await apiClient(
          `enrollment/verify?token=${encodeURIComponent(token)}`,
          "GET",
          null,
          { refresh: false }
        )
        if (cancelled) return
        if (res?.success) {
          setInvite(res.result)
          form.setFieldsValue({
            name: res.result.name,
            hostname: res.result.hostname,
            serialNumber: res.result.expectedSerial,
            osPlatform: res.result.osPlatform || "windows",
          })
          if (res.alreadyEnrolled) {
            setDone({
              hostname: res.result.hostname,
              serialNumber: res.result.expectedSerial,
              already: true,
            })
          }
        } else {
          setError(res?.message || "This enrollment link is invalid or has expired.")
        }
      } catch (err) {
        if (!cancelled) setError("Could not verify this enrollment link.")
      } finally {
        if (!cancelled) setChecking(false)
      }
    }
    verify()
    return () => {
      cancelled = true
    }
  }, [token, form])

  const onFinish = async (values) => {
    setLoading(true)
    setError("")
    try {
      const res = await apiClient(
        "enrollment/accept",
        "POST",
        {
          token,
          name: values.name,
          hostname: values.hostname,
          serialNumber: values.serialNumber,
          osPlatform: values.osPlatform,
        },
        { refresh: false }
      )
      if (res?.success) {
        setDone(res.result)
      } else {
        setError(res?.message || "Could not enroll this device.")
      }
    } catch (err) {
      setError("Could not enroll this device. Please try again.")
    } finally {
      setLoading(false)
    }
  }

  if (!token || (error && !invite && !done)) {
    return (
      <AuthShell
        wide
        badge="Device enrollment"
        title="Enrollment link"
        subtitle="This invitation is missing, invalid, or expired."
      >
        <Alert type="error" showIcon message={error || "Invalid enrollment link"} />
        <div className="text-center mt-3">
          <Link to="/login">Back to sign-in</Link>
        </div>
      </AuthShell>
    )
  }

  if (done) {
    return (
      <AuthShell
        wide
        badge="Device enrollment"
        title="Device enrolled"
        subtitle="Install the communication agent on this computer. After it checks in, your administrator can deploy Xpanse from the console."
        footer="Keep this device on until the communication agent is running."
      >
        <Alert
          type="success"
          showIcon
          message={done.already ? "This device is already enrolled" : "Enrollment accepted"}
          description={
            <span>
              <strong>{done.hostname}</strong>
              {done.serialNumber ? ` · ${done.serialNumber}` : ""}.{" "}
              {done.already
                ? "This device is already reporting to Xpanse."
                : "Run the command below as Administrator so this PC becomes the communication agent."}
            </span>
          }
        />
        {done.commsInstallCommand ? (
          <>
            <Input.TextArea
              className="mt-3"
              value={done.commsInstallCommand}
              autoSize={{ minRows: 3, maxRows: 6 }}
              readOnly
            />
            <Button
              type="primary"
              className="mt-2"
              block
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(done.commsInstallCommand)
                } catch (err) {
                  window.prompt("Copy this command", done.commsInstallCommand)
                }
              }}
            >
              Copy install command
            </Button>
          </>
        ) : done.agentDownloadUrl ? (
          <Button
            type="primary"
            className="mt-3"
            href={done.agentDownloadUrl}
            target="_blank"
            rel="noreferrer"
            block
          >
            Download agent
          </Button>
        ) : (
          <p className="mt-3">
            Check your email for the communication agent command. After it
            checks in, your administrator can deploy Xpanse.
          </p>
        )}
        <div className="text-center mt-3">
          <Link to="/login">Go to Xpanse</Link>
        </div>
      </AuthShell>
    )
  }

  return (
    <AuthShell
      wide
      badge="Device enrollment"
      title="Enroll this device"
      subtitle={
        invite
          ? `${invite.email || "This device"} is invited to register this Windows, Linux, or Mac. You will then install a communication agent.`
          : "Confirm this computer so the communication agent can be installed. Use the steps below to find the computer name and serial number first."
      }
      footer="After you accept, run the communication agent command as Administrator. Your administrator deploys Xpanse from the console."
    >
      {invite?.message ? (
        <Alert className="mb-3" type="info" showIcon message={invite.message} />
      ) : null}
      {invite?.bootstrapCommand ? (
        <Alert
          className="mb-3"
          type="info"
          showIcon
          message="Windows Server / PowerShell"
          description={
            <span>
              On the device, run this as Administrator instead of opening this
              page with irm:
              <Typography.Paragraph className="mt-2 mb-0" copyable code>
                {invite.bootstrapCommand}
              </Typography.Paragraph>
            </span>
          }
        />
      ) : null}
      {error ? (
        <Alert className="mb-3" type="error" showIcon message={error} />
      ) : null}
      <Form form={form} layout="vertical" className="auth-form" onFinish={onFinish} disabled={checking}>
        <Form.Item name="name" label="Your name">
          <Input placeholder="Name" />
        </Form.Item>
        <Form.Item
          name="osPlatform"
          label="Operating system"
          rules={[{ required: true, message: "Select Windows, Linux, or macOS" }]}
        >
          <Radio.Group>
            <Radio value="windows">Windows</Radio>
            <Radio value="linux">Linux</Radio>
            <Radio value="macos">macOS</Radio>
          </Radio.Group>
        </Form.Item>
        <Form.Item
          name="hostname"
          label="Computer name"
          extra="This is the hostname of the computer you are enrolling."
          rules={[{ required: true, message: "Enter this device's hostname" }]}
        >
          <Input prefix={<DesktopOutlined />} placeholder="e.g. LAPTOP-01" />
        </Form.Item>
        <Form.Item
          name="serialNumber"
          label="Device serial number"
          extra={`BIOS / hardware serial for ${lookup.label}.`}
          rules={[{ required: true, message: "Enter the device serial number" }]}
        >
          <Input placeholder="Serial number" />
        </Form.Item>
        <Collapse
          className="enroll-howto"
          defaultActiveKey={["howto"]}
          items={[
            {
              key: "howto",
              label: `How to find these details on ${lookup.label}`,
              children: (
                <div>
                  <p>
                    Look these up on the computer you are enrolling, then paste
                    them above.
                  </p>
                  <p>
                    <strong>Computer name</strong>
                    <br />
                    {lookup.hostname}
                  </p>
                  <Typography.Paragraph
                    className="enroll-howto__cmd"
                    copyable
                    code
                  >
                    {lookup.hostnameCmd}
                  </Typography.Paragraph>
                  <p>
                    <strong>Serial number</strong>
                    <br />
                    {lookup.serial}
                  </p>
                  <Typography.Paragraph
                    className="enroll-howto__cmd"
                    copyable
                    code
                  >
                    {lookup.serialCmd}
                  </Typography.Paragraph>
                  {lookup.serialAltCmd ? (
                    <>
                      <p>{lookup.serialAlt}</p>
                      <Typography.Paragraph
                        className="enroll-howto__cmd"
                        copyable
                        code
                      >
                        {lookup.serialAltCmd}
                      </Typography.Paragraph>
                    </>
                  ) : null}
                </div>
              ),
            },
          ]}
        />
        <Button type="primary" htmlType="submit" loading={loading} block>
          Accept and enroll
        </Button>
      </Form>
    </AuthShell>
  )
}

export default EnrollDevice
